/* eslint-disable max-lines */
import React, { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { createSemanticColors } from '@this/shared/ui/theme/tokens/semantic-tokens';
import A from '@this/shared/atoms/a';

import type { OrderItemStatusKey } from '@this/domain/order_item/order_item_status';
import type Trip from '@this/domain/trip/trip2';
import type Order from '@this/domain/order2';
import type OrderItem from '@this/domain/order_item2';
import type TransportElement from '@this/domain/transport_element2';
import type HotelElement from '@this/domain/hotel_element2';
import type CancelChargeElement from '@this/domain/cancel_charge_element2';
import type BulkTicket from '@this/domain/bulk_ticket2';
import type ShareholderTicketList from '@this/domain/shareholder_ticket_list/index';
import type TaxType from '@this/domain/tax_type2';

import Modal from '@this/shared/modal/modal';
import Confirm from '@this/shared/confirm/confirm';
import Text from '@this/shared/text/text';
import BilledAtEditor from '@this/domain/billed_at_editor/billed_at_editor';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Link } from '@this/src/components/shared/ui/navigations/link';

import { Box } from '@material-ui/core';
import type ChangeFeeElement from '@this/src/domain/change_fee_element2';
import type PaymentMethodList from '@this/src/domain/payment_method/payment_method_list';
import type { ShareholderInfo } from '@this/domain/other_service_shareholder_info';
import type { CabinOption } from '@this/domain/cabin_option';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import { Fetcher, HTTPError } from '@this/src/util';
import type { IndividualDomesticAirSuppliedItem } from '@this/domain/individual_domestic_air_supplied_item';
import { reportError } from '@this/lib/bugsnag';
import type { TimeZone } from '../price_change_form';
import OrderItemStatusForm from './order_item_status_form/order_item_status_form';
import OrderItemWaitingForm from './order_item_waiting_form/order_item_waiting_form';
import { ExicItemDisplay } from './transport_element_form/exic_item_display';
import { UserExicCard } from './transport_element_form/user_exic_card';
import OrderItemBasicInfoForm from './order_item_basic_info_form';
import OrderItemEditForm from './order_item_edit_form';
import OrderItemPriceForm from './order_item_price_form/order_item_price_form';

export type PnrRetrieveAllResponseJson = {
  price_result: PnrRetrievePriceResponseJson[];
  flight_result: PnrRetrieveFlightResponseJson[];
};

export type PnrRetrievePriceResponseJson = {
  name: string;
  price: number;
  tax_type_id: number;
};

export type PnrRetrieveFlightResponseJson = {
  id: string;
  dep_date: string;
  dep_time: string;
  arr_date: string;
  arr_time: string;
  from: string;
  to: string;
  carrier: string;
  flight_number: string;
  d_datetime: string;
  a_datetime: string;
  airport_from_name: string;
  airport_to_name: string;
  from_time_zone: string;
  to_time_zone: string;
  cabin: string;
};

interface Props {
  index: number;
  order: Order;
  orderItem: OrderItem;
  selectedTrip: Trip;
  bulkTickets: BulkTicket[];
  shareholderTickets: ShareholderTicketList;
  taxTypes: TaxType[];
  serviceId: number;
  paymentMethods: PaymentMethodList;
  shareholderInfos: ShareholderInfo[];
  cabinOptions: CabinOption[];
  individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[];
  handleRemoveItem: (orderItem: OrderItem) => void;
  handleRemoveElement: (orderItem: OrderItem, i: number) => void;
  updateIsAirNumbersMissing: (orderItem: OrderItem) => void;
  reloadSelectedTrip: () => void;
  hotelElementProviderOptions: { [key: string]: string };
  carProviderOptions?: Array<[string, string]>;
  carTypeOptions?: Array<[string, string]>;
  shippingCategoryOptions?: { [key: string]: string };
  wifiProviderOptions?: { [key: string]: string };
  updateForeignAirTransportElements: (
    orderItem: OrderItem,
    retrieveResult: PnrRetrieveFlightResponseJson[]
  ) => void;
  updatePriceDetailsFromPnr: (orderItem: OrderItem, retrieveResult: PnrRetrievePriceResponseJson[]) => void;
  timeZones: TimeZone[];
}

const PriceChangeFormItem: React.FC<Props> = ({
  index,
  order,
  orderItem,
  selectedTrip,
  bulkTickets,
  shareholderTickets,
  taxTypes,
  serviceId,
  paymentMethods,
  shareholderInfos,
  cabinOptions,
  individualTargetSuppliedItems,
  individualDomesticAirSuppliedItems,
  handleRemoveItem,
  handleRemoveElement,
  updateIsAirNumbersMissing,
  reloadSelectedTrip,
  hotelElementProviderOptions,
  carProviderOptions,
  carTypeOptions,
  shippingCategoryOptions,
  wifiProviderOptions,
  updateForeignAirTransportElements,
  updatePriceDetailsFromPnr,
  timeZones
}) => {
  const semanticColors = useMemo(() => createSemanticColors(serviceId), [serviceId]);
  const [cancelingHotelId, setCancelingHotelId] = useState<number | null>(null);

  const handleHotelCancelClick = useCallback((id: number, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setCancelingHotelId(id);
  }, []);

  const handleHotelCancelConfirm = useCallback(() => {
    Fetcher.put(`/hotels/${cancelingHotelId}/cancel`, {}).then(
      _result => {
        setCancelingHotelId(null);
        reloadSelectedTrip();
      },
      e => {
        alert(
          e instanceof HTTPError && e.response?.data.error
            ? e.response.data.error
            : '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
        setCancelingHotelId(null);
      }
    );

    setCancelingHotelId(null);
  }, [cancelingHotelId, reloadSelectedTrip]);

  const handleHotelCancelAlertClose = useCallback(() => {
    setCancelingHotelId(null);
  }, []);

  const initialOrderItem = useMemo(() => {
    return selectedTrip.initialOrderItemFor(orderItem);
  }, [selectedTrip, orderItem]);

  const orderItemDiff = orderItem.diffWith(initialOrderItem);

  const classNameForModifiedField = useCallback(
    (...path: (string | number)[]): string => {
      const modified = orderItemDiff ? _.has(orderItemDiff, path) : true;
      return modified ? 'virtual-counte-price-change-form__modified_field' : '';
    },
    [orderItemDiff]
  );

  try {
    return (
      <div>
        <Box display="flex" alignItems="center" marginTop="20px">
          <h4 style={{ marginBottom: 0 }}>商品{index + 1}</h4>
          <Box marginLeft="10px">trace_id: {orderItem.traceId}</Box>
          <Box marginLeft="auto">
            <Link type="button" onClick={() => handleRemoveItem(orderItem)} data-testid="remove-order-item-button">
              この商品を削除×
            </Link>
          </Box>
        </Box>

        <div className="virtual-counte-price-change-form__item-body" data-testid="price-change-form-item">
          {orderItem.isEditing ? (
            <>
              <OrderItemBasicInfoForm
                orderItem={orderItem}
                selectedTrip={selectedTrip}
                classNameForModifiedField={classNameForModifiedField}
              />
              <OrderItemEditForm
                order={order}
                orderItem={orderItem}
                selectedTrip={selectedTrip}
                serviceId={serviceId}
                cabinOptions={cabinOptions}
                individualTargetSuppliedItems={individualTargetSuppliedItems}
                individualDomesticAirSuppliedItems={individualDomesticAirSuppliedItems}
                handleRemoveElement={handleRemoveElement}
                updateIsAirNumbersMissing={updateIsAirNumbersMissing}
                hotelElementProviderOptions={hotelElementProviderOptions}
                carProviderOptions={carProviderOptions}
                carTypeOptions={carTypeOptions}
                shippingCategoryOptions={shippingCategoryOptions}
                wifiProviderOptions={wifiProviderOptions}
                updateForeignAirTransportElements={updateForeignAirTransportElements}
                updatePriceDetailsFromPnr={updatePriceDetailsFromPnr}
                timeZones={timeZones}
              />
              {(orderItem.elements.length > 0 || orderItem.orderItemCategory === 'arrangement_request') && (
                <OrderItemPriceForm
                  index={index}
                  serviceId={serviceId}
                  selectedTrip={selectedTrip}
                  orderItem={orderItem}
                  bulkTickets={bulkTickets}
                  shareholderTickets={shareholderTickets}
                  taxTypes={taxTypes}
                  paymentMethods={paymentMethods}
                  shareholderInfos={shareholderInfos}
                  individualTargetSuppliedItems={individualTargetSuppliedItems}
                  classNameForModifiedField={classNameForModifiedField}
                />
              )}
              <div className="virtual-counte-price-change-form__element-block no-border">
                <div className="virtual-counte-price-change-form__input-wrapper">
                  <Button
                    size="small"
                    onClick={() => {
                      orderItem.isEditing = false;
                    }}
                  >
                    閉じる
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              {orderItem.orderItemCategory === 'arrangement_request' ? (
                <div className="virtual-counte-price-change-form__element-block no-border">
                  <div className="virtual-counte-price-change-form__input-wrapper">
                    <Text text="リクエストフォーム利用料" />
                  </div>
                </div>
              ) : (
                <>
                  {orderItem.elements.map((element, j) => (
                    <div className="virtual-counte-price-change-form__element-block no-border" key={j}>
                      <div className="virtual-counte-price-change-form__input-wrapper">
                        <Text text={element.description} />
                      </div>
                      {(() => {
                        if (element.type === 'hotel') {
                          const hotel = element as HotelElement;
                          return (
                            <>
                              {hotel.provider === 'mynavi' && (
                                <div className="virtual-counte-price-change-form__input-wrapper">
                                  {hotel.apiCanceledAt === null ? (
                                    <>
                                      <div style={{ fontWeight: 'bold', color: semanticColors.brand.primary }}>
                                        予約中
                                      </div>
                                      <A onClick={e => handleHotelCancelClick(hotel.id, e)}>キャンセルする</A>
                                    </>
                                  ) : (
                                    <div style={{ fontWeight: 'bold', color: semanticColors.danger.primary }}>
                                      キャンセル済み
                                    </div>
                                  )}
                                </div>
                              )}
                              {hotel.hotelReservation && (
                                <div>
                                  <p>ホテル予約：{hotel.hotelReservation.statusDescription()}</p>
                                  <p>{hotel.hotelReservation.error && hotel.hotelReservation.error}</p>
                                </div>
                              )}
                            </>
                          );
                        }
                        if (element.type === 'transport') {
                          const transport = element as TransportElement;
                          return (
                            <>
                              <ExicItemDisplay element={transport} orderItem={orderItem} elementIndex={j} />
                              <UserExicCard element={transport} orderItem={orderItem} elementIndex={j} />
                            </>
                          );
                        }
                        if (element.type === 'cancel') {
                          const cancel = element as CancelChargeElement;
                          const traceItems = order.traceItemDescriptions;
                          return (
                            <div>
                              ■キャンセル商品
                              <br />
                              {cancel.mappings.map((mapping, i) => (
                                <p key={`mapping-${i}`}>
                                  trace_id-{mapping.originalTraceId}: {traceItems[mapping.originalTraceId || 0]}
                                </p>
                              ))}
                            </div>
                          );
                        }
                        if (element.type === 'change_fee') {
                          const changeFee = element as ChangeFeeElement;
                          const traceItems = order.traceItemDescriptions;
                          return (
                            <div>
                              ■変更商品
                              <br />
                              {changeFee.mappings.map((mapping, i) => (
                                <p key={`mapping-${i}`}>
                                  trace_id-{mapping.originalTraceId}: {traceItems[mapping.originalTraceId || 0]}
                                </p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ))}
                </>
              )}
              <OrderItemStatusForm
                orderItemId={orderItem.id}
                jobType={orderItem.jobType}
                status={orderItem.status as OrderItemStatusKey}
                onJobTypeChange={value => {
                  orderItem.jobType = value;
                }}
                onStatusChange={value => {
                  orderItem.status = value;
                }}
                fetchData={reloadSelectedTrip}
                showLabel
                style={{ marginBottom: '5px' }}
              />
              <BilledAtEditor
                billedAt={orderItem.billedAt}
                orderItemId={orderItem.id}
                style={{ marginBottom: '5px' }}
                onChange={date => {
                  orderItem.billedAt = date;
                }}
                order={order}
              />
              <OrderItemWaitingForm orderItem={orderItem} showLabel style={{ marginBottom: '5px' }} />
              <Button
                size="small"
                onClick={() => {
                  orderItem.isEditing = true;
                }}
              >
                編集
              </Button>
            </>
          )}
          <div
            style={{ display: 'block', marginTop: '10px', textAlign: 'center' }}
            data-testid="copy-order-item-button"
          >
            <Button color="sub" fullWidth onClick={() => order.handleCopyItem(orderItem)}>
              この商品をコピー
            </Button>
          </div>
        </div>
        {cancelingHotelId && (
          <Modal show hideModal={() => handleHotelCancelAlertClose()}>
            <Confirm
              onConfirmed={() => handleHotelCancelConfirm()}
              onAborted={() => handleHotelCancelAlertClose()}
              message={`ホテルの予約をキャンセルしてよろしいですか？\n※この操作は取り消せません`}
            />
          </Modal>
        )}
      </div>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default observer(PriceChangeFormItem);
